import {useState, useRef} from 'react';
import {format} from 'date-fns';
import {Smartphone, Plus, UserX, Loader2} from 'lucide-react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';
import {createMutationErrorHandler, trpc} from '@common-web/lib/trpc';
import {useToast} from '@common-web/hooks/use-toast';
import {AppInfoField} from '@client/components/InfoFields';
import {AddAppDependentDialog} from '@client/pages/client/member-profile/AddAppDependentDialog';
import {MemberStatusBadge} from '@client/pages/client/member-profile/MemberStatusBadge';

type AppUserData = NonNullable<ReturnType<typeof trpc.vba.appUser.getByMember.useQuery>['data']>;

const APP_NAMES: Record<AppUserData['source'], string> = {
  hcues: 'HealthCues',
  qtm: 'Quantum',
  champ: 'CHAMP',
  synrgy: 'Synrgy',
};

const formatAppDate = (date: Date | string | null | undefined) => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'MMM d, yyyy');
  } catch {
    return 'N/A';
  }
};

export const AppUserCard = ({
  subscriberId,
  externalTpaMemberId,
  hasAppUserAffiliation,
  primaryIsActive,
}: {
  /** A CBS member's VBA subscriber. Absent for an external-TPA member. */
  subscriberId?: string;
  /** An Alpha/Elysian member's EDI id. These have no VBA subscriber. */
  externalTpaMemberId?: string;
  hasAppUserAffiliation: boolean;
  primaryIsActive?: boolean;
}) => {
  const {toast} = useToast();

  const [deactivateUserOpen, setDeactivateUserOpen] = useState(false);
  // `isPending` only updates on the next render, so a rapid double click would send two requests.
  const deactivateInFlight = useRef(false);
  const [addDepOpen, setAddDepOpen] = useState(false);

  // The two routes resolve the same account through different identities; everything
  // below this point is identical.
  const identity = externalTpaMemberId ? {externalTpaMemberId} : {subscriberId: subscriberId!};
  const isExternalTpaMember = !!externalTpaMemberId;

  const vbaAppUserQuery = trpc.vba.appUser.getByMember.useQuery(
    {subscriberId: subscriberId!},
    {enabled: !isExternalTpaMember && !!subscriberId && hasAppUserAffiliation},
  );
  const tpaAppUserQuery = trpc.vba.appUser.getByExternalTpaMember.useQuery(
    {memberId: externalTpaMemberId!},
    {enabled: isExternalTpaMember},
  );
  const appUserQuery = isExternalTpaMember ? tpaAppUserQuery : vbaAppUserQuery;

  const deactivateAppUserMutation = trpc.vba.appUser.deactivateUser.useMutation({
    onSuccess: () => {
      toast({
        title: 'App User Deleted',
        description: 'The app account has been deleted.',
      });
      setDeactivateUserOpen(false);
      appUserQuery.refetch();
    },
    onError: createMutationErrorHandler('Failed to delete app user'),
    onSettled: () => {
      deactivateInFlight.current = false;
    },
  });

  if (!hasAppUserAffiliation) return null;

  if (appUserQuery.isLoading) {
    return (
      <div className='flex items-center justify-center py-8'>
        <Loader2 className='h-6 w-6 animate-spin text-brand-navy' />
      </div>
    );
  }

  if (appUserQuery.error) {
    return (
      <Alert variant='destructive'>
        <AlertDescription>
          {appUserQuery.error.message || 'Failed to load app user data'}
        </AlertDescription>
      </Alert>
    );
  }

  if (!appUserQuery.data) return null;

  const {appUser, familyAppUsers, source, canManage, canDeactivate} = appUserQuery.data;

  // Synrgy app users come from the CleverBenefits v2 model, which has no dependents.
  const supportsDependents = source !== 'synrgy';

  // A dependent hangs off an existing app account, so there has to be one and it has to
  // still be usable. Reaching this line already proves the account exists — the card
  // returns null above when it doesn't — so what remains is that the account hasn't
  // been deactivated, and that the employee's own coverage hasn't lapsed.
  const canAddDependent =
    supportsDependents && canManage && !appUser.isDeactivated && primaryIsActive !== false;
  const appName = APP_NAMES[source];

  return (
    <>
      <Card className='border-0 shadow-lg bg-white overflow-hidden'>
        <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2 flex-wrap'>
              <CardTitle className='text-lg font-semibold text-brand-navy flex items-center gap-2'>
                <Smartphone className='h-5 w-5' />
                App User
              </CardTitle>
              <Badge variant='outline' className='text-xs'>
                {source.toUpperCase()}
              </Badge>
              {appUser.relationshipToEmployee && (
                <Badge className='bg-brand-blue/10 text-brand-blue border-brand-blue/20 text-xs'>
                  {appUser.relationshipToEmployee}
                </Badge>
              )}
              <MemberStatusBadge
                className={appUser.isDeactivated ? 'border-0 bg-red-100 text-red-800' : undefined}
                label={appUser.isDeactivated ? 'Deleted' : 'Active'}
                status={appUser.isDeactivated ? 'inactive' : 'active'}
              />
              <Badge
                className={
                  appUser.emailVerified
                    ? 'bg-emerald/10 text-emerald border-emerald/20 text-xs'
                    : 'bg-amber-100 text-amber-800 border-0 text-xs'
                }
              >
                {appUser.emailVerified ? 'Email Verified' : 'Email Unverified'}
              </Badge>
              {familyAppUsers.length > 0 && (
                <Badge variant='outline' className='text-xs'>
                  +{familyAppUsers.length} family account
                  {familyAppUsers.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            {canDeactivate && !appUser.isDeactivated && (
              <Button
                variant='outline'
                size='sm'
                className='text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700'
                onClick={() => setDeactivateUserOpen(true)}
              >
                <UserX className='h-4 w-4 mr-1' />
                Delete user
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className='p-6 space-y-6'>
          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4'>
            <AppInfoField label='Email' value={appUser.email} />
            <AppInfoField label='Locale' value={appUser.locale} />
            <AppInfoField label='Registered' value={formatAppDate(appUser.createdDate)} />
            <AppInfoField label='Updated' value={formatAppDate(appUser.updatedDate)} />
            <AppInfoField
              label='HRA Completed'
              value={
                appUser.hraCompletedDate ? formatAppDate(appUser.hraCompletedDate) : 'Not completed'
              }
            />
            <div>
              <AppInfoField
                label='Notifications'
                value={
                  appUser.pushEnabled === null
                    ? 'N/A'
                    : appUser.pushEnabled
                      ? 'Enabled'
                      : 'Disabled'
                }
              />
              {appUser.enabledPushTypes.length > 0 && (
                <p className='mt-1 text-xs text-muted-foreground'>
                  {appUser.enabledPushTypes.join(', ')}
                </p>
              )}
            </div>
            {appUser.clhUserId && <AppInfoField label='CLH User ID' value={appUser.clhUserId} />}
          </div>

          {appUser.eligibilityDetails && (
            <div className='pt-4 border-t'>
              <h4 className='text-sm font-semibold text-brand-navy mb-3'>Eligibility Details</h4>
              <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                <AppInfoField label='Group ID' value={appUser.eligibilityDetails.groupId} />
                <AppInfoField label='Last Name' value={appUser.eligibilityDetails.lastName} />
                <AppInfoField label='Zip Code' value={appUser.eligibilityDetails.zipCode} />
                {appUser.eligibilityDetails.lastClhSync && (
                  <AppInfoField
                    label='Last CLH Sync'
                    value={formatAppDate(appUser.eligibilityDetails.lastClhSync)}
                  />
                )}
              </div>
            </div>
          )}

          {/* Who else in the household has the app. Reads the account collection only —
              a dependent the employee added in the app, a pet, or anyone who never
              registered belongs to the dependents section above this card, which reads
              VBA enrollment. Mixing the two listed a registered spouse twice. */}
          <div className='pt-4 border-t'>
            <div className='flex items-center justify-between mb-3'>
              {/* The household list below is built from the VBA family roster, which an
                  external-TPA member has no equivalent of, so only the heading and the
                  add control apply to them. */}
              <h4 className='text-sm font-semibold text-brand-navy'>
                {isExternalTpaMember ? 'App Dependents' : 'Other App Accounts'}
              </h4>
              {canAddDependent && (
                <Button variant='outline' size='sm' onClick={() => setAddDepOpen(true)}>
                  <Plus className='h-4 w-4 mr-1' />
                  Add dependent
                </Button>
              )}
            </div>
            {isExternalTpaMember ? (
              <p className='text-sm text-muted-foreground'>
                Dependents on this app account are listed in the Dependents section above.
              </p>
            ) : familyAppUsers.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                No one else in this family has an app account.
              </p>
            ) : (
              <div className='space-y-2'>
                {familyAppUsers.map((member) => (
                  <div
                    key={member.id}
                    className='flex items-center justify-between gap-3 border rounded-lg px-4 py-3'
                  >
                    <div className='flex items-center gap-3 flex-wrap'>
                      <span className='font-medium text-brand-navy text-sm'>
                        {member.firstName || member.lastName
                          ? `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim()
                          : member.externalUniqueId}
                      </span>
                      <Badge variant='outline' className='text-xs'>
                        {member.relationshipToEmployee}
                      </Badge>
                      <Badge
                        className={
                          member.emailVerified
                            ? 'bg-emerald text-white border-0 text-xs'
                            : 'bg-amber-100 text-amber-800 border-0 text-xs'
                        }
                      >
                        {member.emailVerified ? 'Email Verified' : 'Email Unverified'}
                      </Badge>
                    </div>
                    <span className='text-sm text-muted-foreground truncate'>{member.email}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <AlertDialog
        open={deactivateUserOpen}
        onOpenChange={(open) => {
          if (!deactivateAppUserMutation.isPending) setDeactivateUserOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete app user?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete this user&apos;s account for the {appName} app. It will not affect
              their enrollment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deactivateAppUserMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (deactivateInFlight.current) return;
                deactivateInFlight.current = true;
                deactivateAppUserMutation.mutate(identity);
              }}
              disabled={deactivateAppUserMutation.isPending}
              className='bg-red-600 hover:bg-red-700 text-white'
            >
              {deactivateAppUserMutation.isPending && (
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
              )}
              Delete user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AddAppDependentDialog
        open={addDepOpen}
        onClose={() => setAddDepOpen(false)}
        onSuccess={() => appUserQuery.refetch()}
        subscriberId={subscriberId}
        externalTpaMemberId={externalTpaMemberId}
      />
    </>
  );
};
